// /invite?t=<token>: where the "Accept the invitation" button in the invite
// email lands. Public (no sign-in): the token is the proof. Shows who invited
// them to which company, takes their name and a password, creates their login
// in that company (100-api functions/team), then signs them straight in.
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { XCircle } from "lucide-react";
import { acceptInvite, getInvite } from "../api";
import { useAuth } from "../auth/AuthContext";
import * as cognito from "../auth/cognito";
import { BrandMark } from "../components/BrandMark";

export function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [invite, setInvite] = useState<{ email: string; company: string; invitedBy: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Who invited them, to which company (or why the link doesn't work)
  useEffect(() => {
    if (!token) {
      setError("This invite link is missing its code. Open it again from the email.");
      return;
    }
    getInvite(token)
      .then((r) => setInvite(r.invite))
      .catch((e: Error) => setError(e.message));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!invite) return;
    setFormError(null);
    if (password !== confirm) {
      setFormError("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await acceptInvite(token, first.trim(), last.trim(), password);
      // Their login now exists with this password: sign them in and go to the console
      const result = await cognito.signIn(invite.email, password);
      if ("challenge" in result) throw new Error("Your account is ready. Please sign in.");
      setSession(result);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setSaving(false);
    }
  }

  return (
    <div className="confirm-page">
      <div className="confirm-card card invite-card">
        <BrandMark size={34} nameSize={24} />
        {error ? (
          <>
            <XCircle size={36} className="confirm-page-bad" aria-hidden="true" />
            <h1>This invite can't be used</h1>
            <p>{error}</p>
            <a className="btn btn-secondary" href="/login">
              Go to sign in
            </a>
          </>
        ) : !invite ? (
          <p className="muted">Checking your invite…</p>
        ) : (
          <>
            <h1>Join {invite.company || "your team"} on xenquote</h1>
            <p>
              {invite.invitedBy ? `${invite.invitedBy} invited you.` : "You've been invited."} Choose a password for <strong>{invite.email}</strong> to get started.
            </p>
            <form className="invite-form" onSubmit={(e) => void submit(e)}>
              <div className="invite-names">
                <label>
                  <span>First name</span>
                  <input className="input" value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" maxLength={60} required autoFocus />
                </label>
                <label>
                  <span>Last name</span>
                  <input className="input" value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" maxLength={60} />
                </label>
              </div>
              <label>
                <span>Password</span>
                <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required />
              </label>
              <label>
                <span>Confirm password</span>
                <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" minLength={8} required />
              </label>
              <p className="invite-hint">At least 8 characters, with an upper case letter, a lower case letter and a number.</p>
              {formError && (
                <div className="flash flash-danger" role="alert">
                  {formError}
                </div>
              )}
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Setting up your account…" : "Accept and sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
